import React, { useState, useEffect } from 'react';
import { kycAPI } from '../../api';
import LoadingSpinner from '../LoadingSpinner';
import QRCodeUpload from '../QRCodeUpload';

const UserKYC = () => {
  const [kycData, setKycData] = useState(null);
  const [formData, setFormData] = useState({
    accountHolder: '',
    accountNumber: '',
    bankName: '',
    branchName: '',
    ifscCode: '',
    panNumber: '',
    aadhaarNumber: '',
    upiNumber: '',
    upiId: '',
    qrCode: ''
  });
    const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchKycStatus();
  }, []);

  const fetchKycStatus = async () => {
    try {
      setLoading(true);
      const response = await kycAPI.getKycStatus();
      
      if (response?.data?.success) {
        setKycData(response.data.data);
        if (response.data.data.kycDetails) {
          setFormData({
            accountHolder: response.data.data.bankDetails?.accountHolder || '',
            accountNumber: response.data.data.bankDetails?.accountNumber || '',
            bankName: response.data.data.bankDetails?.bankName || '',
            branchName: response.data.data.bankDetails?.branchName || '',
            ifscCode: response.data.data.bankDetails?.ifscCode || '',
            panNumber: response.data.data.kycDetails?.panNumber || '',
            aadhaarNumber: response.data.data.kycDetails?.aadhaarNumber || '',
            upiNumber: response.data.data.kycDetails?.upiNumber || '',
            upiId: response.data.data.kycDetails?.upiId || '',
            qrCode: response.data.data.kycDetails?.qrCode || ''
          });
        }
      }
      setError('');
    } catch (err) {
      setError('Failed to load KYC status');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleQrCodeUpload = (qrCodeUrl) => {
    setFormData(prev => ({
      ...prev,
      qrCode: qrCodeUrl
    }));
  };

  const handleQrCodeSubmit = async () => {
    // QR code is now optional; no need to check for presence

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const response = await kycAPI.updateQrCode(formData.qrCode);
      
      if (response?.data?.success) {
        setSuccess(response.data.message);
        // Refresh KYC status
        await fetchKycStatus();
      } else {
        setError(response?.data?.error || 'Failed to update QR code');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update QR code. Please try again.');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    // Validate QR code is uploaded
    // QR code is now optional; no need to check for presence

    try {
      let response;
      if (kycData?.kycStatus === 'REJECTED') {
        response = await kycAPI.updateKyc(formData);
      } else if (kycData?.kycStatus === 'SUBMITTED') {
        // Only update QR code when KYC is submitted
        response = await kycAPI.updateQrCode(formData.qrCode);
      } else {
        response = await kycAPI.submitKyc(formData);
      }

      if (response?.data?.success) {
        setSuccess(response.data.message);
        // Refresh KYC status
        await fetchKycStatus();
      } else {
        setError(response?.data?.error || 'Failed to submit KYC');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit KYC. Please try again.');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'text-yellow-600';
      case 'SUBMITTED': return 'text-blue-600';
      case 'VERIFIED': return 'text-green-600';
      case 'REJECTED': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'PENDING': return 'bg-yellow-100 text-yellow-800';
      case 'SUBMITTED': return 'bg-blue-100 text-blue-800';
      case 'VERIFIED': return 'bg-green-100 text-green-800';
      case 'REJECTED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner fullPage={false} />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-secondary">KYC Verification</h2>
        <button
          onClick={fetchKycStatus}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* KYC Status Card */}
      {kycData && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h3 className="text-xl font-bold text-secondary mb-4">KYC Status</h3>
          <div className="flex items-center justify-between">
            <div>
              <span className="text-gray-600">Current Status: </span>
              <span className={`font-semibold ${getStatusColor(kycData.kycStatus)}`}>
                {kycData.kycStatus}
              </span>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(kycData.kycStatus)}`}>
              {kycData.kycStatus}
            </span>
          </div>
          
          {kycData.kycDetails?.submittedAt && (
            <div className="mt-2 text-sm text-gray-500">
              Submitted on: {new Date(kycData.kycDetails.submittedAt).toLocaleDateString()}
            </div>
          )}
          
          {kycData.kycDetails?.verifiedAt && (
            <div className="mt-2 text-sm text-gray-500">
              Verified on: {new Date(kycData.kycDetails.verifiedAt).toLocaleDateString()}
            </div>
          )}
          
          {kycData.kycDetails?.rejectionReason && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">
                <strong>Rejection Reason:</strong> {kycData.kycDetails.rejectionReason}
              </p>
            </div>
          )}
        </div>
      )}

      {/* KYC Form */}
      {(kycData?.kycStatus === 'PENDING' || kycData?.kycStatus === 'REJECTED' || kycData?.kycStatus === 'SUBMITTED') && (
        <div className="bg-white rounded-lg shadow-md p-8">
          <h3 className="text-xl font-bold text-secondary mb-6">
            {kycData?.kycStatus === 'REJECTED' ? 'Update KYC Details' : 
             kycData?.kycStatus === 'SUBMITTED' ? 'Update KYC Details (QR Code Only)' : 
             'Submit KYC Details'}
          </h3>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800">{success}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Bank Details Section */}
            {kycData?.kycStatus !== 'SUBMITTED' && (
              <div>
                <h4 className="text-lg font-semibold text-secondary mb-4">Bank Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Account Holder Name *
                    </label>
                    <input
                      type="text"
                      name="accountHolder"
                      value={formData.accountHolder}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Account Number *
                    </label>
                    <input
                      type="text"
                      name="accountNumber"
                      value={formData.accountNumber}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bank Name *
                    </label>
                    <input
                      type="text"
                      name="bankName"
                      value={formData.bankName}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Branch Name *
                    </label>
                    <input
                      type="text"
                      name="branchName"
                      value={formData.branchName}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      IFSC Code *
                    </label>
                    <input
                      type="text"
                      name="ifscCode"
                      value={formData.ifscCode}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent uppercase"
                      required
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Show read-only bank details when KYC is submitted */}
            {kycData?.kycStatus === 'SUBMITTED' && kycData?.bankDetails && (
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h4 className="text-lg font-semibold text-secondary mb-4">Bank Details (Locked)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div><strong>Account Holder:</strong> {kycData.bankDetails.accountHolder}</div>
                  <div><strong>Account Number:</strong> {kycData.bankDetails.accountNumber}</div>
                  <div><strong>Bank Name:</strong> {kycData.bankDetails.bankName}</div>
                  <div><strong>Branch Name:</strong> {kycData.bankDetails.branchName}</div>
                  <div><strong>IFSC Code:</strong> {kycData.bankDetails.ifscCode}</div>
                </div>
                <p className="text-xs text-gray-500 mt-3">Bank details are locked after submission. Contact admin for changes.</p>
              </div>
            )}

            {/* Personal Details Section */}
            {kycData?.kycStatus !== 'SUBMITTED' && (
              <div>
                <h4 className="text-lg font-semibold text-secondary mb-4">Personal Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      PAN Number *
                    </label>
                    <input
                      type="text"
                      name="panNumber"
                      value={formData.panNumber}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent uppercase"
                      placeholder="ABCDE1234F"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Aadhaar Number *
                    </label>
                    <input
                      type="text"
                      name="aadhaarNumber"
                      value={formData.aadhaarNumber}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="123456789012"
                      maxLength={12}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      UPI Number
                    </label>
                    <input
                      type="text"
                      name="upiNumber"
                      value={formData.upiNumber}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="9876543210"
                      maxLength={10}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      UPI ID
                    </label>
                    <input
                      type="text"
                      name="upiId"
                      value={formData.upiId}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent lowercase"
                      placeholder="yourname@upi"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Show read-only personal details when KYC is submitted */}
            {kycData?.kycStatus === 'SUBMITTED' && kycData?.kycDetails && (
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h4 className="text-lg font-semibold text-secondary mb-4">Personal Details (Locked)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div><strong>PAN Number:</strong> {kycData.kycDetails.panNumber}</div>
                  <div><strong>Aadhaar Number:</strong> {kycData.kycDetails.aadhaarNumber}</div>
                  <div><strong>UPI Number:</strong> {kycData.kycDetails.upiNumber || 'Not provided'}</div>
                  <div><strong>UPI ID:</strong> {kycData.kycDetails.upiId || 'Not provided'}</div>
                </div>
                <p className="text-xs text-gray-500 mt-3">Personal details are locked after submission. Contact admin for changes.</p>
              </div>
            )}

            {/* QR Code Section */}
            <div>
              <h4 className="text-lg font-semibold text-secondary mb-4">QR Code for Withdrawals</h4>
              <QRCodeUpload 
                onQrCodeUpload={handleQrCodeUpload}
                existingQrCode={formData.qrCode}
              />
            </div>

            
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting...' : (kycData?.kycStatus === 'REJECTED' ? 'Update KYC' : kycData?.kycStatus === 'SUBMITTED' ? 'Update QR Code' : 'Submit KYC')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Verified KYC Display */}
      {kycData?.kycStatus === 'VERIFIED' && (
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="text-center">
            <div className="mb-6">
              <svg className="w-16 h-16 text-green-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-green-600 mb-2">KYC Verified!</h3>
            <p className="text-gray-600 mb-6">Your KYC has been successfully verified.</p>
            
            {kycData.bankDetails && (
              <div className="text-left bg-gray-50 rounded-lg p-6 mb-6">
                <h4 className="font-semibold text-secondary mb-4">Verified Bank Details</h4>
                <div className="space-y-2 text-sm">
                  <p><strong>Account Holder:</strong> {kycData.bankDetails.accountHolder}</p>
                  <p><strong>Bank:</strong> {kycData.bankDetails.bankName}</p>
                  <p><strong>Branch:</strong> {kycData.bankDetails.branchName}</p>
                  <p><strong>Account:</strong> {kycData.bankDetails.accountNumber}</p>
                  <p><strong>IFSC:</strong> {kycData.bankDetails.ifscCode}</p>
                </div>
              </div>
            )}

            {kycData.kycDetails?.qrCode ? (
              <div className="text-left bg-gray-50 rounded-lg p-6 mb-6">
                <h4 className="font-semibold text-secondary mb-4">QR Code for Withdrawals</h4>
                <div className="flex items-center space-x-4">
                  <img
                    src={kycData.kycDetails.qrCode}
                    alt="QR Code"
                    className="w-24 h-24 object-cover border-2 border-gray-300 rounded-lg"
                  />
                  <div className="text-sm text-gray-600">
                    <p className="font-medium text-green-600 mb-1">QR Code Verified</p>
                    <p>This QR code will be used for withdrawal payments</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-left bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
                <h4 className="font-semibold text-secondary mb-4">QR Code Required for Withdrawals</h4>
                <div className="space-y-4">
                  <div className="text-sm text-gray-700">
                    <p className="font-medium text-yellow-600 mb-2">⚠️ QR Code Missing</p>
                    <p>You need to upload your QR code to be able to request withdrawals from your main wallet.</p>
                  </div>
                  
                  {error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  )}
                  
                  {success && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-600">{success}</p>
                    </div>
                  )}
                  
                  <div>
                    <QRCodeUpload 
                      onQrCodeUpload={handleQrCodeUpload}
                      existingQrCode={formData.qrCode}
                    />
                  </div>
                  
                  {formData.qrCode && (
                    <div className="flex justify-end">
                      <button
                        onClick={handleQrCodeSubmit}
                        disabled={submitting}
                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {submitting ? 'Updating...' : 'Update QR Code'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserKYC;
